import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetAccountantSubscription } from '@assetaccountant/api/models/asset-accountant-subscription';
import { CountryDto } from '@assetaccountant/api/models/country.dto';
import { Feature } from '@assetaccountant/api/models/feature';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { PackagePricingMethod } from '@assetaccountant/api/models/package-pricing-method';
import { PackageType } from '@assetaccountant/api/models/package-type';
import { ProductId } from '@assetaccountant/api/models/product-id';
import { SubscriptionPackageOptions } from '@assetaccountant/api/models/subscription-package-options';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { RegisterApi } from '@assetaccountant/api/services/register.api';
import { currencyData } from '@assetaccountant/core/models/currencies';
import { OrganisationType } from '@assetaccountant/core/models/enum/organisation-type';
import { getPackageTypeDisplayName } from '@assetaccountant/core/models/enum/package-display';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';
import { FeatureFlag } from '@assetaccountant/core/services/feature-flag';
import { OrganisationService } from '@assetaccountant/core/services/organisation.service';
import { SubscriptionService } from '@assetaccountant/core/services/subscription.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { TypedNgbModal, setupModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddPaymentMethodModalComponent } from '../../navigation/add-payment-method-modal/add-payment-method-modal.component';
import { ConfirmChangePlanModalComponent } from '../../navigation/view-pricing-modal/confirm-change-plan-modal/confirm-change-plan-modal.component';
import { PricingConsumptionDisplayModel } from './pricing-display-model';

@Component({
  selector: 'app-subscription-packages',
  templateUrl: './subscription-packages.component.html',
  styleUrls: ['./subscription-packages.component.scss'],
  standalone: false,
})
export class SubscriptionPackagesComponent implements OnInit {
  @Input() destinationRouteCommand: string;
  @Input() newOrganisation: boolean;

  @Output() close = new EventEmitter();
  @Output() packageUpdated = new EventEmitter();
  @Output() updating = new EventEmitter<boolean>();
  readonly currentlyAvailablePaidPackages = [
    PackageType.BusinessLiteV2,
    PackageType.BusinessBasicV2,
    PackageType.BusinessStandardV2,
    PackageType.AccountingV2Lite,
    PackageType.AccountingV2Basic,
    PackageType.AccountingV2Standard,
  ];
  readonly Feature = Feature;
  readonly FeatureFlag = FeatureFlag;
  readonly PackageType = PackageType;
  readonly OrganisationPermissionType = OrganisationPermissionType;

  accountantPricing = false;
  activeOrganisation: OrganisationDto;
  countries: CountryDto[] = [];
  currencySymbol: string;
  displayCurrency: string;
  isCalculatorOpen = false;
  isTrialPlan = false;
  isFreePlan = false;
  prices: Partial<Record<PackageType, PricingConsumptionDisplayModel | null>>;
  selectedPackage?: PackageType;
  subscription: AssetAccountantSubscription;
  upgradeOptions: SubscriptionPackageOptions;

  get hasSubscriptionCurrency(): boolean {
    return !(
      this.subscription?.packagePriceCurrency === null ||
      this.subscription?.packagePriceCurrency === undefined ||
      this.subscription?.packagePriceCurrency === ''
    );
  }

  get showAccountingFirmPlans() {
    return this.subscription?.organisationType === OrganisationType.AccountingFirm;
  }

  get freePlanMaxAssets() {
    return (
      this.upgradeOptions?.options.find(o => o.packageType === PackageType.Free)?.maxAssets ?? 10
    );
  }

  private _isUpdating = false;
  get isUpdating() {
    return this._isUpdating;
  }

  set isUpdating(value: boolean) {
    this._isUpdating = value;
    this.updating.emit(value);
  }

  get showFreePlan() {
    return this.isTrialPlan || this.isFreePlan;
  }

  get canUseFreePlan() {
    return this.showFreePlan && this.subscription?.totalAssets <= this.freePlanMaxAssets;
  }

  constructor(
    private router: Router,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
    private subscriptionService: SubscriptionService,
    private appNavigationService: AppNavigationService,
    private organisationService: OrganisationService,
    private trackingService: TrackingService,
    @Inject(ActivatedRoute) private route: ActivatedRoute,
    private registerApi: RegisterApi,
    private organisationApi: OrganisationV2Api,
  ) {}

  ngOnInit() {
    this.activeOrganisation = this.appNavigationService.getActiveOrganisation();
    this.registerApi
      .getCountries({ includeCountryId: this.activeOrganisation.billingCountryId || undefined })
      .subscribe(result => {
        this.countries = result;
      });

    this.subscriptionService.getSubscription(
      this.appNavigationService.getActiveOrganisationId(true),
    );

    const subscription$ = !this.newOrganisation
      ? this.subscriptionService.subscription
      : of(null as AssetAccountantSubscription | null);

    const upgradeOptions$ = this.organisationApi
      .getSubscriptionPackageOptions({ shallowCheck: false })
      .pipe(map(({ upgradeOptions }) => upgradeOptions));

    combineLatest([subscription$, upgradeOptions$]).subscribe(([subscription, upgradeOptions]) => {
      this.upgradeOptions = upgradeOptions;

      if (upgradeOptions) {
        const prices = this.currentlyAvailablePaidPackages.reduce(
          (acc, packageType) => {
            acc[packageType] = this.getConsumptionPricing(packageType);
            return acc;
          },
          {} as Partial<Record<PackageType, PricingConsumptionDisplayModel | null>>,
        );
        this.prices = prices;
      }

      if (subscription) {
        const sub = subscription as AssetAccountantSubscription;
        this.subscription = sub;
        this.displayCurrency = sub.packagePriceCurrency || 'USD';
        this.currencySymbol =
          currencyData.find(currency => currency.code === this.displayCurrency)?.symbol || '$';

        this.isTrialPlan = sub.packageType == null;
        this.isFreePlan =
          sub.packageType === PackageType.Free || sub.packageType === PackageType.LegacyFree;

        if (this.isFreePlan || sub.packagePricingMethod === PackagePricingMethod.BusinessTrial) {
          if (sub.organisationType === OrganisationType.AccountingFirm) {
            this.accountantPricing = true;
          }
        } else {
          if (
            sub.packagePricingMethod === PackagePricingMethod.Accounting ||
            sub.packagePricingMethod === PackagePricingMethod.AccountingMultiProduct
          ) {
            this.accountantPricing = true;
          }
        }
      }

      this.updateSelectedPackage(false);
    });
  }

  changePlan(packageType: PackageType) {
    const features = this.upgradeOptions.options.find(p => p.packageType === packageType)?.features;
    const usedFeaturesNotInPlan = this.upgradeOptions.featuresUsed.filter(
      f => !features?.includes(f),
    );
    if (usedFeaturesNotInPlan.length) {
      setupModal(
        this.ngbModal.open(ConfirmChangePlanModalComponent, { size: 'md' }),
        modal => {
          modal.usedFeaturesNotInPlan = usedFeaturesNotInPlan;
        },
        modal => modal.confirm.subscribe(() => this.setPackage(packageType)),
      );
    } else {
      this.setPackage(packageType);
    }
  }

  onClose() {
    if (this.destinationRouteCommand) {
      this.router.navigateByUrl(this.destinationRouteCommand);
    }
    this.close.emit();
  }

  onToggleCalculator() {
    this.isCalculatorOpen = !this.isCalculatorOpen;

    if (this.isCalculatorOpen) {
      // Set selected package before opening calculator
      this.updateSelectedPackage(true);
    }
  }

  isFeatureInUse(feature: Feature) {
    return this.upgradeOptions?.featuresUsed.includes(feature);
  }

  isPackageRecommended(packageType: PackageType | PackageType[]) {
    if (this.upgradeOptions?.currentSubscription) {
      if (this.upgradeOptions.currentSubscription.totalAssets < this.freePlanMaxAssets) {
        return false;
      }
      if (!Array.isArray(packageType)) {
        packageType = [packageType];
      }
      const _packageType = Array.isArray(packageType) ? packageType : [packageType];

      const matchingPackages = this.upgradeOptions?.options.filter(
        p => p.packageType && _packageType.includes(p.packageType),
      );

      return !!matchingPackages?.find(p => p.isRecommended);
    }
    return false;
  }

  setPackage(packageType: PackageType) {
    if (packageType === this.subscription?.packageType) {
      // Don't do anything if package is already selected
      return;
    }

    this.isUpdating = true;

    if (!this.subscription?.hasPaymentMethod && packageType !== PackageType.Free) {
      // Converting to paid subscription
      const paymentModalRef = setupModal(
        this.ngbModal.open(AddPaymentMethodModalComponent),
        modal => {
          modal.activeOrganisation = this.activeOrganisation;
          modal.paymentCallback = () => {
            this.onPackageUpdated();

            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { action: 'subscribed' },
              queryParamsHandling: 'merge',
            });
            setTimeout(() => {
              this.trackingService.trackEvent(
                'New paying subscription',
                getPackageTypeDisplayName(packageType),
              );
            }, 0);
          };

          modal.startSubscriptionPackageType = packageType;
          modal.newOrganisation = this.newOrganisation;
        },
      );

      paymentModalRef.dismissed.subscribe(() => {
        this.isUpdating = false;
      });
    } else if (this.isFreePlan) {
      // Setting free subscription
      this.organisationService
        .createSubscription(this.appNavigationService.getActiveOrganisationId(), packageType)
        .subscribe(() => {
          this.onPackageUpdated();
        });
    } else {
      // Updating subscription
      this.organisationService
        .updateSubscription(this.appNavigationService.getActiveOrganisationId(), packageType)
        .subscribe(() => {
          this.trackingService.trackEvent(
            'Update paid subscription',
            getPackageTypeDisplayName(packageType),
          );
          this.onPackageUpdated();
        });
    }
  }

  togglePricing() {
    this.accountantPricing = !this.accountantPricing;

    this.updateSelectedPackage(this.isCalculatorOpen);
  }

  private onPackageUpdated() {
    this.subscriptionService.reset();

    // Refresh subscription
    this.subscriptionService.getSubscription().subscribe();
    this.packageUpdated.emit();

    if (this.destinationRouteCommand) {
      this.router.navigateByUrl(this.destinationRouteCommand);
    }
    this.close.emit();
    this.isUpdating = false;
  }

  private getConsumptionPricing(packageType: PackageType): PricingConsumptionDisplayModel | null {
    const option = this.upgradeOptions?.options.find(p => p.packageType === packageType);
    if (option) {
      const assets = option.packageProducts?.find(p => p.productId === ProductId.AssetConsumption);
      return {
        assetPrice: assets?.pricing[0]?.unitPrice || 0,
        assetUnits: assets?.objectUnits || 0,
      };
    }
    return null;
  }

  private updateSelectedPackage(isCalculatorOpen: boolean) {
    if (isCalculatorOpen) {
      // Swap for equivelant business/accounting package

      if (this.accountantPricing) {
        switch (this.selectedPackage) {
          case PackageType.BusinessBasicV2:
            this.selectedPackage = PackageType.AccountingV2Basic;
            break;
          case PackageType.BusinessStandardV2:
            this.selectedPackage = PackageType.AccountingV2Standard;
            break;
          case PackageType.BusinessLiteV2:
            this.selectedPackage = PackageType.AccountingV2Lite;
            break;
        }
      } else {
        switch (this.selectedPackage) {
          case PackageType.AccountingV2Basic:
            this.selectedPackage = PackageType.BusinessBasicV2;
            break;
          case PackageType.AccountingV2Standard:
            this.selectedPackage = PackageType.BusinessStandardV2;
            break;
          case PackageType.AccountingV2Lite:
            this.selectedPackage = PackageType.BusinessLiteV2;
            break;
        }
      }
    } else {
      // Default selected package

      // Only default to current package if it's a valid paid package
      let validCurrentPackage: PackageType | undefined;
      if (
        this.subscription?.packageType &&
        this.currentlyAvailablePaidPackages.includes(this.subscription.packageType)
      ) {
        validCurrentPackage = this.subscription.packageType;
      }
      this.selectedPackage =
        validCurrentPackage ||
        // Recommended package
        this.upgradeOptions.availableOptions.find(option => option.isRecommended)?.packageType ||
        // Lite
        (this.accountantPricing ? PackageType.AccountingV2Lite : PackageType.BusinessLiteV2);
    }
  }
}
